import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import ChartLegend from "./charts/ChartLegend";
import ChartTooltip from "./charts/ChartTooltip";
import { formatCompactUsd, nivoTheme, parseChartDate } from "./charts/chartTheme";

const isHiddenByDefault = (serie) =>
  serie.visible === false || serie.visible === "false";

// Time series on a real time axis: points are spaced by actual time, gaps
// stay gaps (no invented zeros). Series: [{ id, name, color, visible,
// data: [{ x: "dd-MM-yyyy HH:mm" | Date, y }] }].
const LineChart = ({ data, height = { xs: 280, md: 360 }, valueFormat = formatCompactUsd }) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [hidden, setHidden] = useState(() => new Set());

  useEffect(() => {
    setHidden(new Set((data || []).filter(isHiddenByDefault).map((s) => s.id)));
  }, [data]);

  const seriesById = useMemo(
    () => Object.fromEntries((data || []).map((s) => [s.id, s])),
    [data]
  );

  const visibleSeries = useMemo(
    () =>
      (data || [])
        .filter((s) => !hidden.has(s.id))
        .map((s) => ({
          id: s.id,
          data: s.data
            .filter((p) => p.y !== null && p.y !== undefined)
            .map((p) => ({ x: parseChartDate(p.x), y: Number(p.y) })),
        }))
        .filter((s) => s.data.length > 0),
    [data, hidden]
  );

  const toggle = (id) =>
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const showLegend = (data || []).length > 1;

  return (
    <Box>
      <Box sx={{ height }}>
        {visibleSeries.length === 0 ? (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {data?.length ? "All series are hidden" : "No volume data for this period"}
            </Typography>
          </Box>
        ) : (
          <ResponsiveLine
            data={visibleSeries}
            theme={nivoTheme}
            colors={(serie) => seriesById[serie.id]?.color}
            margin={{ top: 12, right: 16, bottom: 32, left: 56 }}
            xScale={{ type: "time", precision: "minute", useUTC: false }}
            yScale={{ type: "linear", min: 0, max: "auto" }}
            curve="monotoneX"
            lineWidth={2}
            enablePoints={false}
            enableGridX={false}
            gridYValues={5}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              format: "%d %b",
              tickValues: isPhone ? "every 4 days" : "every 2 days",
              tickSize: 0,
              tickPadding: 10,
            }}
            axisLeft={{
              format: valueFormat,
              tickValues: 5,
              tickSize: 0,
              tickPadding: 8,
            }}
            enableSlices="x"
            sliceTooltip={({ slice }) => (
              <ChartTooltip
                title={format(slice.points[0].data.x, "d MMM yyyy, HH:mm")}
                rows={[...slice.points]
                  .sort((a, b) => b.data.y - a.data.y)
                  .map((point) => ({
                    id: point.id,
                    color: point.serieColor,
                    label: seriesById[point.serieId]?.name ?? point.serieId,
                    value: valueFormat(point.data.y),
                  }))}
              />
            )}
            animate={false}
          />
        )}
      </Box>
      {showLegend && (
        <ChartLegend
          items={data.map((s) => ({
            id: s.id,
            label: s.name,
            color: s.color,
            hidden: hidden.has(s.id),
          }))}
          onToggle={toggle}
        />
      )}
    </Box>
  );
};

export default LineChart;
